/**
 * A Least Recently Used (LRU) Cache implementation with maximum size limit.
 * When the cache exceeds maxSize, the least recently used items are evicted.
 */
export class LRUCache<K, V> {
    private cache: Map<K, V>
    private readonly maxSize: number

    constructor(maxSize: number = 1000) {
        this.cache = new Map<K, V>()
        this.maxSize = maxSize
    }

    /**
     * Get a value from the cache. This marks the item as recently used.
     * @param key The key to look up
     * @returns The value if found, undefined otherwise
     */
    get(key: K): V | undefined {
        const value = this.cache.get(key)
        if (value !== undefined) {
            // Delete and re-insert to move to end (most recent)
            this.cache.delete(key)
            this.cache.set(key, value)
        }
        return value
    }

    /**
     * Get a value from the cache without updating its position (no LRU reordering).
     * Use this for frequent read-only operations like hover events.
     * @param key The key to look up
     * @returns The value if found, undefined otherwise
     */
    peek(key: K): V | undefined {
        return this.cache.get(key)
    }

    /**
     * Set a value in the cache. This marks the item as recently used.
     * @param key The key to store
     * @param value The value to store
     */
    set(key: K, value: V): void {

        if (this.maxSize === 0) {
            return
        }

        // If key already exists, delete it first so it gets re-added at the end
        if (this.cache.has(key)) {
            this.cache.delete(key)
        } else if (this.cache.size >= this.maxSize) {
            // Remove the least recently used item (first item in Map)
            const firstKey = this.cache.keys().next().value
            if (firstKey !== undefined) {
                this.cache.delete(firstKey)
            }
        }

        this.cache.set(key, value)
    }

    /**
     * Check if a key exists in the cache
     * @param key The key to check
     * @returns true if the key exists, false otherwise
     */
    has(key: K): boolean {
        return this.cache.has(key)
    }

    /**
     * Clear all items from the cache
     */
    clear(): void {
        this.cache.clear()
    }

    /**
     * Get the current size of the cache
     * @returns The number of items in the cache
     */
    get size(): number {
        return this.cache.size
    }

    /**
     * Delete a specific key from the cache
     * @param key The key to delete
     * @returns true if the key existed and was deleted, false otherwise
     */
    delete(key: K): boolean {
        return this.cache.delete(key)
    }
}